// © Thibault Sabathier, 2026. frontend/src/utils/browserNotification.js
//
// Notification navigateur, tolérante aux plateformes. NE LÈVE JAMAIS.
//
// ── POURQUOI CE FICHIER ────────────────────────────────────────────────────
// L'API `Notification` n'existe pas partout et ne s'utilise pas partout de la
// même façon. Les deux pannes constatées en production (Sentry) :
//
//  - iOS / Safari n'expose pas `Notification` hors PWA installée. Lire
//    `Notification.permission` y lève un ReferenceError « Can't find variable:
//    Notification ».
//  - Chrome sur Android INTERDIT `new Notification()` dès qu'un service worker
//    contrôle la page : « Failed to construct 'Notification': Illegal
//    constructor. Use ServiceWorkerRegistration.showNotification() instead ».
//    Terrilink étant une PWA, le constructeur y échoue systématiquement.
//
// L'API était appelée à nu à quatre endroits de SocketContext. Chaque échec
// interrompait le reste du gestionnaire socket, donc l'ouverture automatique de
// la conversation qui suivait immédiatement.
//
// ── LE CLIC ────────────────────────────────────────────────────────────────
// Une notification affichée PAR le service worker n'a pas de `onclick` côté
// page : c'est l'écouteur `notificationclick` de public/sw-push.js qui la
// traite, à partir de `data`. On lui passe donc exactement le contrat déjà
// utilisé par le push serveur :
//   - message      : { type: 'message', conversationId }  → le SW poste
//                    OPEN_CONVERSATION, PushDeepLinkHandler ouvre la fenêtre
//                    flottante ;
//   - autres types : { type, url }                        → le SW poste
//                    PUSH_NOTIFICATION_CLICKED, la navigation se fait côté React.
// `onClick` n'est donc utilisé que par le repli `new Notification()`, c'est à
// dire un navigateur de bureau sans service worker actif.

// Le constructeur existe-t-il sur cette plateforme ?
export function notificationsSupported() {
  return typeof window !== 'undefined' && 'Notification' in window
}

// Permission accordée ? Répond 'denied' plutôt que de lever là où l'API manque.
export function notificationPermission() {
  if (!notificationsSupported()) return 'denied'
  try {
    return window.Notification.permission
  } catch {
    return 'denied'
  }
}

// Le service worker qui contrôle la page, ou null. On passe par
// getRegistration() et NON par `navigator.serviceWorker.ready` : cette dernière
// ne se résout jamais quand aucun service worker n'est enregistré, ce qui
// laisserait la promesse en suspens indéfiniment.
async function controllingRegistration() {
  try {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null
    const registration = await navigator.serviceWorker.getRegistration()
    return registration && typeof registration.showNotification === 'function' ? registration : null
  } catch {
    return null
  }
}

/**
 * Affiche une notification navigateur si la plateforme et l'utilisateur le
 * permettent. Aucune exception ne remonte à l'appelant : un échec de
 * notification ne doit jamais interrompre le traitement métier qui l'entoure.
 *
 * @param {object}   params
 * @param {string}   params.title            - Titre affiché
 * @param {string}   [params.body]           - Corps du message
 * @param {string}   [params.icon]           - URL de l'icône
 * @param {string}   [params.tag]            - Regroupe/remplace les notifications
 * @param {object}   [params.data]           - Contrat de clic lu par le service
 *                                             worker (cf. en-tête du fichier)
 * @param {Function} [params.onClick]        - Repli bureau uniquement
 * @param {boolean}  [params.onlyWhenUnfocused=true] - Ne rien afficher si
 *                   l'utilisateur regarde déjà la page
 * @returns {Promise<boolean>} true si une notification a été affichée
 */
export async function showBrowserNotification({
  title,
  body,
  icon,
  tag,
  data = {},
  onClick,
  onlyWhenUnfocused = true,
} = {}) {
  // Le `= {}` n'est pas cosmétique. La déstructuration de l'argument est
  // évaluée AVANT le try ci-dessous : un appel sans argument renverrait une
  // promesse rejetée, et comme aucun appelant n'attend le retour, ça
  // remonterait en unhandledrejection. Cette fonction promet de ne jamais
  // casser le flux de son appelant ; la promesse doit tenir par construction,
  // pas par la discipline des trois sites d'appel actuels.
  try {
    if (!title || notificationPermission() !== 'granted') return false

    if (onlyWhenUnfocused && typeof document !== 'undefined') {
      const userIsLooking = !document.hidden && document.hasFocus()
      if (userIsLooking) return false
    }

    // `renotify` exige un `tag` : sans lui, le navigateur rejette l'appel.
    const options = {
      body,
      icon,
      tag,
      data,
      ...(tag ? { renotify: true } : {}),
    }

    // Chemin service worker : obligatoire sur Android, valable partout ailleurs.
    const registration = await controllingRegistration()
    if (registration) {
      await registration.showNotification(title, options)
      return true
    }

    // Repli bureau : pas de service worker, le constructeur est autorisé.
    const notification = new window.Notification(title, options)
    if (typeof onClick === 'function') {
      notification.onclick = () => {
        try {
          window.focus()
          onClick()
        } catch (err) {
          console.warn('[Notification] clic non traité:', err?.message)
        } finally {
          notification.close()
        }
      }
    }
    return true
  } catch (err) {
    // Plateforme récalcitrante, permission révoquée entre-temps, quota système :
    // on trace et on rend la main. L'appelant continue son travail.
    console.warn('[Notification] affichage impossible:', err?.message)
    return false
  }
}
